import { Image, StyleSheet, Text, View } from '@react-pdf/renderer'
import {
  PerformanceAgreementQuestionCommentFragment,
  PerformanceAgreementQuestionnaireAnswerFragment,
  PerformanceAgreementQuestionnaireAnsweredQuestionFragment,
  PerformanceAgreementQuestionnaireQuestionFragment,
  PerformanceAgreementQuestionnaireSectionFragment,
  PerformanceAgreementQuestionnaireSubjectFragment,
  PerformanceAgreementQuestionnairesFragment,
  PerformanceReviewerStrategiesStrategyEnum,
} from 'generated/resources/graphql'
import { compact, keyBy } from 'lodash'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { Section, SectionTitle } from './_Blocks'

const ratingLabel = (
  question: PerformanceAgreementQuestionnaireQuestionFragment,
  rating?: number | null
) => {
  return question?.scale?.find((scale) => scale.value === rating)?.text
}

const styles = StyleSheet.create({
  section: {
    display: 'flex',
    flexDirection: 'row',
    gap: 20,
    paddingVertical: 10,
    borderBottom: '0.5px solid #ccc',
    width: '100%',
  },
  noBorder: {
    border: 0,
  },
  access: {
    marginVertical: 10,
    display: 'flex',
    flexDirection: 'row',
    gap: 20,
    paddingVertical: 10,
    borderBottom: '0.5px solid #ccc',
    width: '100%',
  },
})

// Convert HTML to plain text for PDF rendering - focus on line breaks and lists
const TextAnswerDisplay = ({ text }: { text: string }) => {
  const cleanText = (text ?? '')
    // Handle headings (h1-h6) - add newline after closing tag
    .replace(/<h[1-6][^>]*>/gi, '') // Remove heading opening tags
    .replace(/<\/h[1-6]>/gi, '\n') // Add newline after heading closing tags

    // Handle lists - keep bullet points and respect original spacing
    .replace(/<ul[^>]*>/gi, '') // Remove ul opening tags
    .replace(/<\/ul>/gi, '\n') // Remove ul closing tags
    .replace(/<ol[^>]*>/gi, '') // Remove ol opening tags
    .replace(/<\/ol>/gi, '') // Remove ol closing tags

    // Handle <p> tags inside <li> - remove them without adding newlines
    .replace(/<li[^>]*><p[^>]*>/gi, '\n• ') // Convert <li><p> to bullet point
    .replace(/<\/p><\/li>/gi, '') // Remove </p></li> without newlines

    // Handle remaining <li> tags (for lists without <p>)
    .replace(/<li[^>]*>/gi, '\n• ') // Convert list items to bullet points
    .replace(/<\/li>/gi, '') // Remove closing li tags

    // Clean up extra newlines within lists - remove double newlines between list items
    .replace(/(\n• [^\n]+)\n\n(\n• [^\n]+)/g, '$1\n$2') // Remove double newlines between list items

    // Handle paragraphs (but not inside lists)
    .replace(/<p[^>]*>\s*<\/p>/gi, '\n') // Convert empty <p> to newlines
    .replace(/<p[^>]*>/gi, '') // Remove <p> opening tags
    .replace(/<\/p>/gi, '\n') // Convert </p> to single newlines

    // Handle line breaks
    .replace(/<br\s*\/?>/gi, '\n') // Convert <br> to newlines

    // Handle code blocks - add newlines before and after
    .replace(/<pre[^>]*>/gi, '\n') // Add newline before <pre>
    .replace(/<\/pre>/gi, '\n') // Add newline after </pre>
    .replace(/<code[^>]*>/gi, '') // Remove <code> opening tags
    .replace(/<\/code>/gi, '') // Remove </code> closing tags

    // Handle blockquotes - add newlines before and after
    .replace(/<blockquote[^>]*>/gi, '\n') // Add newline before <blockquote>
    .replace(/<\/blockquote>/gi, '\n') // Add newline after </blockquote>

    // Handle horizontal rules - add newline
    .replace(/<hr\s*\/?>/gi, '\n') // Convert <hr> to newlines

    // Remove all other HTML tags (including formatting, links, etc.)
    .replace(/<[^>]*>/g, '')

    // Handle HTML entities
    .replace(/&nbsp;/g, ' ') // Convert &nbsp; to spaces
    .replace(/&amp;/g, '&') // Convert &amp; to &
    .replace(/&lt;/g, '<') // Convert &lt; to <
    .replace(/&gt;/g, '>') // Convert &gt; to >
    .replace(/&quot;/g, '"') // Convert &quot; to "
    .replace(/&#39;/g, "'") // Convert &#39; to '

    // Clean up extra whitespace and newlines - be more conservative
    .replace(/\n\s*\n\s*\n+/g, '\n\n') // Remove 3+ consecutive empty lines, keep double newlines
    .replace(/^\s+|\s+$/g, '') // Trim leading/trailing whitespace
    .trim()

  return <Text>{cleanText}</Text>
}

const Answer: React.FC<{
  question: PerformanceAgreementQuestionnaireQuestionFragment
  answer?: PerformanceAgreementQuestionnaireAnswerFragment
}> = ({ question, answer }) => {
  switch (question.answerType) {
    case 'text':
      return <TextAnswerDisplay text={answer?.answerText ?? ''} />
    case 'multiple_choice':
      return <TextAnswerDisplay text={answer?.answerChoice?.join(', ') ?? ''} />
    case 'number':
      return <Text>{answer?.answerFloat || answer?.answerInt}</Text>
    case 'rating':
      return (
        <View style={{ display: 'flex' }}>
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <Text style={{ fontWeight: 'bold' }}>{answer?.answerRating?.value}:</Text>
            <Text> {ratingLabel(question, answer?.answerRating?.value)}</Text>
          </View>
          <View style={{ display: 'flex' }}>
            <TextAnswerDisplay text={answer?.answerRating?.comment ?? ''} />
          </View>
        </View>
      )
    default:
      const exhaustiveCheck: never = question.answerType
      throw new Error(`Unhandled question type: ${exhaustiveCheck}`)
  }
}

const AnswerContent: React.FC<{
  answeredQuestion: PerformanceAgreementQuestionnaireAnsweredQuestionFragment
  evaluationType: PerformanceReviewerStrategiesStrategyEnum
  comment?: PerformanceAgreementQuestionCommentFragment
}> = ({ answeredQuestion, comment }) => {
  return (
    <View style={{ flex: 1, width: '100%', display: 'flex' }}>
      <View style={{ display: 'flex', marginBottom: 5 }}>
        <Text style={{ fontWeight: 'bold' }}>{answeredQuestion.question.title}</Text>
      </View>
      <Answer answer={answeredQuestion.answer || undefined} question={answeredQuestion.question} />
      {comment?.text && (
        <View style={{ display: 'flex', marginBottom: 5, marginTop: 5 }}>
          <Text>
            <Text style={{ fontWeight: 'bold' }}>
              {i18n.t('performance_process.agreements.comments.title')}:{' '}
            </Text>
            {comment?.text}
          </Text>
        </View>
      )}
    </View>
  )
}

const QuestionnaireContent: React.FC<{
  content: PerformanceAgreementQuestionnaireSectionFragment['content']
  comments: PerformanceAgreementQuestionCommentFragment[]
  evaluationType: PerformanceReviewerStrategiesStrategyEnum
}> = ({ content, comments, evaluationType }) => {
  const commentByQuestionUuid = keyBy(comments, (c) => c.questionUuid)

  return (
    <View>
      {content.map((element, i) => {
        const isLastSection = i === content.length - 1

        return (
          <Section key={element.uuid}>
            {element.type === 'answered_section' && (
              <SectionTitle>{element.sectionTitle}</SectionTitle>
            )}
            {element.questions.map((answeredQuestion, j) => {
              const isLastQuestion = j === element.questions.length - 1

              return (
                <View
                  key={answeredQuestion.question.uuid}
                  style={compact([
                    styles.section,
                    isLastSection && isLastQuestion && styles.noBorder,
                  ])}
                >
                  <AnswerContent
                    evaluationType={evaluationType}
                    answeredQuestion={answeredQuestion}
                    comment={commentByQuestionUuid[answeredQuestion.question.uuid]}
                  />
                </View>
              )
            })}
          </Section>
        )
      })}
    </View>
  )
}

const SignerHeader: React.FC<{
  signer: PerformanceAgreementQuestionnaireSubjectFragment
}> = ({ signer }) => (
  <View style={{ width: '100%', flex: 1, flexDirection: 'row', alignItems: 'center' }}>
    {signer.avatar && (
      <Image
        src={buildUrl(signer.avatar)}
        style={{ width: '32', borderRadius: 5, marginRight: '10px' }}
      />
    )}
    <View style={{ flex: 1 }}>
      <Text style={{ fontWeight: 'bold' }}>{signer.fullName}</Text>
      <Text>{signer.employee?.referenceContract?.jobTitle}</Text>
    </View>
  </View>
)

export const Questionnaires: React.FC<{
  questionnaires: PerformanceAgreementQuestionnairesFragment
}> = ({ questionnaires }) => {
  const selfAnswers = questionnaires.selfEvaluation?.answeredQuestionnaires
  const selfQuestions = selfAnswers?.answeredQuestionnaireWithSections?.content ?? []
  const selfEmployeeScore = selfAnswers?.answeredEmployeeScoreQuestionnaire?.content ?? []
  const selfCompetencies = selfAnswers?.answeredCompetenciesQuestionnaire?.content ?? []

  const managerAnswers = questionnaires.managerEvaluation?.answeredQuestionnaires
  const managerQuestions = managerAnswers?.answeredQuestionnaireWithSections?.content ?? []
  const managerEmployeeScore = managerAnswers?.answeredEmployeeScoreQuestionnaire?.content ?? []
  const managerCompetencies = managerAnswers?.answeredCompetenciesQuestionnaire?.content ?? []

  return (
    <Section>
      <SectionTitle>
        {i18n.t('performance_process.agreements.printable_pdf.review_answers')}
      </SectionTitle>

      <View style={styles.section}>
        {questionnaires.managerEvaluation?.reviewerAccess && (
          <SignerHeader signer={questionnaires.managerEvaluation.reviewerAccess} />
        )}
        {questionnaires.target?.access && <SignerHeader signer={questionnaires.target.access} />}
      </View>

      {(managerCompetencies.length > 0 || selfCompetencies.length > 0) && (
        <View style={styles.section}>
          {managerCompetencies.length > 0 && (
            <View style={{ flex: 1 }}>
              <QuestionnaireContent
                content={managerCompetencies}
                comments={questionnaires.managerComments}
                evaluationType="manager"
              />
            </View>
          )}

          {selfCompetencies.length > 0 && (
            <View style={{ flex: 1 }}>
              <QuestionnaireContent
                content={selfCompetencies}
                comments={questionnaires.selfComments}
                evaluationType="self"
              />
            </View>
          )}
        </View>
      )}

      {(managerQuestions.length > 0 || selfQuestions.length > 0) && (
        <View style={styles.section}>
          {managerQuestions.length > 0 && (
            <View style={{ flex: 1 }}>
              <QuestionnaireContent
                content={managerQuestions}
                comments={questionnaires.managerComments}
                evaluationType="manager"
              />
            </View>
          )}

          {selfQuestions.length > 0 && (
            <View style={{ flex: 1 }}>
              <QuestionnaireContent
                content={selfQuestions}
                comments={questionnaires.selfComments}
                evaluationType="self"
              />
            </View>
          )}
        </View>
      )}

      {(managerEmployeeScore.length > 0 || selfEmployeeScore.length > 0) && (
        <View style={styles.section}>
          {managerEmployeeScore.length > 0 && (
            <View style={{ flex: 1 }}>
              <QuestionnaireContent
                content={managerEmployeeScore}
                comments={questionnaires.managerComments}
                evaluationType="manager"
              />
            </View>
          )}

          {selfEmployeeScore.length > 0 && (
            <View style={{ flex: 1 }}>
              <QuestionnaireContent
                content={selfEmployeeScore}
                comments={questionnaires.selfComments}
                evaluationType="self"
              />
            </View>
          )}
        </View>
      )}
    </Section>
  )
}
